import { useCallback, useEffect, useRef } from "react";
import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "./layout/AppLayout";
import { NotificationCenter } from "./components/NotificationCenter";
import { JobNotificationsWatcher } from "./components/JobNotificationsWatcher";
import { useApiHealth } from "./api/hooks";
import { useAuthStore } from "./store/authStore";
import { initKeycloakClient, resetKeycloakInstance } from "./auth/keycloakClient";
import type Keycloak from "keycloak-js";

function AuthBootstrapper() {
  const setAuthRequired = useAuthStore((state) => state.setAuthRequired);
  const setProvider = useAuthStore((state) => state.setProvider);
  const setAuth = useAuthStore((state) => state.setAuth);
  const setAuthLoading = useAuthStore((state) => state.setAuthLoading);
  const setOidcConfig = useAuthStore((state) => state.setOidcConfig);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const { data } = useApiHealth();
  const keycloakRef = useRef<Keycloak | null>(null);
  const refreshTimerRef = useRef<number | null>(null);

  const cleanupTimer = useCallback(() => {
    if (refreshTimerRef.current !== null) {
      window.clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const syncKeycloakState = useCallback(
    (kc: Keycloak) => {
      const token = kc.token;
      if (!token) {
        clearAuth("expired");
        return;
      }
      const payload = kc.tokenParsed;
      const expiresAt = payload?.exp ? new Date(payload.exp * 1000).toISOString() : null;
      const username =
        payload?.preferred_username ?? payload?.email ?? payload?.name ?? payload?.sub ?? null;
      const realmRoles = Array.isArray((payload as any)?.realm_access?.roles)
        ? (payload as any).realm_access.roles.map((r: unknown) => String(r))
        : [];
      const resourceAccess = (payload as any)?.resource_access as Record<string, { roles?: unknown[] }> | undefined;
      const clientRoles = resourceAccess
        ? Object.values(resourceAccess).flatMap((entry) =>
            Array.isArray(entry?.roles) ? entry.roles.map((r) => String(r)) : []
          )
        : [];
      const roles = Array.from(new Set([...realmRoles, ...clientRoles]));
      setAuth(token, expiresAt, username, roles);
    },
    [clearAuth, setAuth]
  );

  useEffect(() => {
    if (!data) {
      return;
    }
    const provider = (data.auth_provider ?? "local") === "keycloak" ? "keycloak" : "local";
    setProvider(provider);
    setAuthRequired(data.auth_required);
    setOidcConfig(data.oidc_issuer ?? null, data.oidc_client_id ?? null);

    if (!data.auth_required) {
      cleanupTimer();
      keycloakRef.current = null;
      resetKeycloakInstance();
      clearAuth("expired");
      setAuthLoading(false);
      return;
    }

    if (provider !== "keycloak") {
      cleanupTimer();
      keycloakRef.current = null;
      resetKeycloakInstance();
      setAuthLoading(false);
      return;
    }

    const issuer = data.oidc_issuer;
    const clientId = data.oidc_client_id;
    if (!issuer || !clientId) {
      console.error("OIDC metadata missing from health response");
      cleanupTimer();
      keycloakRef.current = null;
      resetKeycloakInstance();
      clearAuth("expired");
      setAuthLoading(false);
      return;
    }

    if (keycloakRef.current) {
      setAuthLoading(false);
      syncKeycloakState(keycloakRef.current);
      return;
    }

    setAuthLoading(true);
    let cancelled = false;

    initKeycloakClient({ issuer, clientId })
      .then((kc) => {
        if (cancelled) {
          return;
        }
        keycloakRef.current = kc;
        syncKeycloakState(kc);

        kc.onAuthSuccess = () => syncKeycloakState(kc);
        kc.onAuthRefreshSuccess = () => syncKeycloakState(kc);
        kc.onAuthLogout = () => {
          cleanupTimer();
          keycloakRef.current = null;
          resetKeycloakInstance();
          clearAuth("expired");
        };
        kc.onAuthError = () => {
          cleanupTimer();
          keycloakRef.current = null;
          resetKeycloakInstance();
          clearAuth("expired");
        };
        kc.onTokenExpired = async () => {
          try {
            const refreshed = await kc.updateToken(60);
            if (refreshed) {
              syncKeycloakState(kc);
            }
          } catch (error) {
            console.error("Keycloak token refresh failed", error);
            cleanupTimer();
            clearAuth("expired");
            await kc.login();
          }
        };

        cleanupTimer();
        refreshTimerRef.current = window.setInterval(async () => {
          try {
            const refreshed = await kc.updateToken(60);
            if (refreshed) {
              syncKeycloakState(kc);
            }
          } catch (error) {
            console.error("Keycloak background refresh failed", error);
          }
        }, 20000);

        setAuthLoading(false);
      })
      .catch((error) => {
        console.error("Failed to initialize Keycloak", error);
        cleanupTimer();
        keycloakRef.current = null;
        resetKeycloakInstance();
        clearAuth("expired");
        setAuthLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    cleanupTimer,
    clearAuth,
    data,
    setAuth,
    setAuthLoading,
    setAuthRequired,
    setOidcConfig,
    setProvider,
    syncKeycloakState
  ]);

  useEffect(() => () => cleanupTimer(), [cleanupTimer]);

  return null;
}

export function App() {
  return (
    <BrowserRouter basename="/ui">
      <AuthBootstrapper />
      <JobNotificationsWatcher />
      <AppRoutes />
      <NotificationCenter />
    </BrowserRouter>
  );
}
